import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, TouchableOpacity, StatusBar, StyleProp, ViewStyle, StyleSheet, StatusBarStyle, Animated } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { DbNavBarProps } from './DbNavBar.types';
import { styles } from './DbNavBar.styles';
import { useDbTheme } from '../../core';

// 兼容 LinearGradient 导入
let LinearGradient: any;
try {
    LinearGradient = require('react-native-linear-gradient').default;
} catch {
    LinearGradient = null;
}

/**
 * 十六进制颜色转 RGBA
 * 支持 #RGB / #RRGGBB / #RRGGBBAA 格式
 */
function hexToRgba(hex: string, alpha: number = 1): string {
    let r = 0, g = 0, b = 0;
    const h = hex.replace('#', '');

    if (h.length === 3) {
        r = parseInt(h[0] + h[0], 16);
        g = parseInt(h[1] + h[1], 16);
        b = parseInt(h[2] + h[2], 16);
    } else if (h.length >= 6) {
        r = parseInt(h.substring(0, 2), 16);
        g = parseInt(h.substring(2, 4), 16);
        b = parseInt(h.substring(4, 6), 16);
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * 将任意颜色格式转为带指定 alpha 的 rgba 字符串
 * 支持 hex (#fff, #667eea) 和 rgba(r,g,b,a) 格式
 */
function colorWithAlpha(color: string, alpha: number): string {
    // 已经是 rgba/rgb 格式
    const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbaMatch) {
        return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${alpha})`;
    }
    // hex 格式
    if (color.startsWith('#')) {
        return hexToRgba(color, alpha);
    }
    // 兜底
    return color;
}

/**
 * 根据背景色自动推断状态栏样式
 * 浅色背景 → dark-content，深色背景 → light-content
 */
function inferBarStyle(bgColor: string): StatusBarStyle {
    const lightColors = ['#fff', '#ffffff', '#f5f5f5', '#fafafa', '#f2f2f7', '#f0f0f0'];
    return lightColors.includes(bgColor.toLowerCase()) ? 'dark-content' : 'light-content';
}

const DbNavBar: React.FC<DbNavBarProps> = ({
    title,
    leftIcon,
    rightIcon,
    onLeftPress,
    onRightPress,
    backgroundColor,
    titleColor,
    titleStyle,
    style,
    useLinearGradient,
    gradientColors: propGradientColors,
    gradientProps = {},
    customContent,
    renderContent,
    useTheme: propUseTheme,
    barStyle,
    contentStyle,
    navHeight = 44,
    fadeOnScroll = false,
    fadeMode = 'fadeOut',
    fadeDistance = 150,
    scrollY,
    scrolledContent,
}) => {
    const insets = useSafeAreaInsets();
    const theme = useDbTheme();

    // 从主题获取导航栏配置（props 优先级高于主题配置）
    const navBarTheme = theme.navBar;
    const finalUseLinearGradient = useLinearGradient ?? navBarTheme.useLinearGradient;
    const finalUseTheme = propUseTheme ?? navBarTheme.useTheme;
    const gradientColors = propGradientColors ?? navBarTheme.gradientColors;

    // ====== 滚动渐变：背景色 alpha（需 setState 因为 LinearGradient 不支持 Animated） ======
    // fadeMode='fadeOut'：alpha 1→0（不透明→透明）
    // fadeMode='fadeIn'：alpha 0→1（透明→不透明）
    const isFadeIn = fadeMode === 'fadeIn';
    const [fadeAlpha, setFadeAlpha] = useState(isFadeIn ? 0 : 1);

    useEffect(() => {
        if (!fadeOnScroll || !scrollY) { return; }

        const listenerId = scrollY.addListener(({ value }) => {
            const progress = Math.min(Math.max(value / fadeDistance, 0), 1);
            setFadeAlpha(isFadeIn ? progress : 1 - progress);
        });

        return () => scrollY.removeListener(listenerId);
    }, [fadeOnScroll, scrollY, fadeDistance, isFadeIn]);

    // ====== 滚动渐变：内容 opacity（纯 Animated，零 setState） ======
    // 原始内容的 opacity：fadeOut 模式 1→0，fadeIn 模式保持 1（始终可见）
    const fadeOutOpacity = useMemo(() => {
        if (!fadeOnScroll || !scrollY) { return undefined; }
        if (isFadeIn) {
            // fadeIn 模式：原始内容始终可见（不需要淡出）
            // 但如果有 scrolledContent，则原始内容需要淡出给 scrolledContent 让位
            return scrollY.interpolate({
                inputRange: [0, fadeDistance],
                outputRange: [1, 0],
                extrapolate: 'clamp',
            });
        }
        return scrollY.interpolate({
            inputRange: [0, fadeDistance],
            outputRange: [1, 0],
            extrapolate: 'clamp',
        });
    }, [fadeOnScroll, scrollY, fadeDistance, isFadeIn]);

    // scrolledContent 的 opacity：始终是 0→1
    const fadeInOpacity = useMemo(() => {
        if (!fadeOnScroll || !scrollY) { return undefined; }
        return scrollY.interpolate({
            inputRange: [0, fadeDistance],
            outputRange: [0, 1],
            extrapolate: 'clamp',
        });
    }, [fadeOnScroll, scrollY, fadeDistance]);

    // ====== 计算最终渐变颜色 ======
    const resolvedGradientColors = (fadeOnScroll && scrollY)
        ? gradientColors.map(c => colorWithAlpha(c, fadeAlpha))
        : gradientColors;

    // ====== 主题色计算 ======
    const resolvedBgColor = backgroundColor
        ?? (finalUseTheme ? theme.colors.background : '#fff');

    const resolvedTitleColor = titleColor
        ?? (finalUseTheme ? theme.colors.text : '#333');

    const resolvedBarStyle: StatusBarStyle = barStyle
        ?? (finalUseLinearGradient ? 'light-content' : inferBarStyle(resolvedBgColor));

    const statusBarHeight = insets.top;

    // 动态样式（根据 navHeight）
    const dynamicNavRowStyle = { height: navHeight };
    const dynamicIconAreaStyle = { height: navHeight };

    // ====== 导航栏内容 ======
    const NavContent = renderContent
        ? renderContent(theme)
        : customContent
            ? customContent
            : (
                <View style={[styles.navRow, dynamicNavRowStyle, contentStyle]}>
                    <TouchableOpacity style={[styles.iconArea, dynamicIconAreaStyle]} onPress={onLeftPress} activeOpacity={0.7}>
                        {leftIcon}
                    </TouchableOpacity>
                    <Text
                        style={[styles.title, { color: resolvedTitleColor }, titleStyle]}
                        numberOfLines={1}
                    >
                        {title}
                    </Text>
                    <TouchableOpacity style={[styles.iconArea, dynamicIconAreaStyle]} onPress={onRightPress} activeOpacity={0.7}>
                        {rightIcon}
                    </TouchableOpacity>
                </View>
            );

    const WrappedContent = (renderContent || customContent)
        ? <View style={[styles.navRow, dynamicNavRowStyle, contentStyle]}>{NavContent}</View>
        : NavContent;

    // ====== 渲染内容层（支持交叉渐变） ======
    const renderContentLayer = () => {
        // 没有启用滚动渐变，直接渲染原始内容
        if (!fadeOnScroll || !scrollY) {
            return WrappedContent;
        }

        // fadeIn 模式没有 scrolledContent：原始内容始终可见，不做任何淡入淡出
        if (isFadeIn && !scrolledContent) {
            return WrappedContent;
        }

        // fadeOut 模式没有 scrolledContent：原始内容随滚动淡出
        if (!isFadeIn && !scrolledContent) {
            return WrappedContent;
        }

        // 有 scrolledContent：两层叠加，交叉渐变
        return (
            <View style={{ position: 'relative' }}>
                {/* 层1：原始内容 —— 渐渐消失 */}
                <Animated.View style={{ opacity: fadeOutOpacity }}>
                    {WrappedContent}
                </Animated.View>

                {/* 层2：滚动后内容 —— 渐渐出现（绝对定位叠在上面） */}
                <Animated.View
                    style={{
                        ...StyleSheet.absoluteFillObject,
                        opacity: fadeInOpacity,
                        justifyContent: 'center',
                    }}
                >
                    <View style={[styles.navRow, dynamicNavRowStyle, contentStyle]}>
                        {scrolledContent}
                    </View>
                </Animated.View>
            </View>
        );
    };

    // ====== 渐变模式 ======
    if (finalUseLinearGradient && LinearGradient) {
        // 检查是否所有颜色都是透明的
        const isAllTransparent = resolvedGradientColors.every(color => {
            const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
            if (rgbaMatch) {
                return parseFloat(rgbaMatch[4]) === 0;
            }
            return color === 'transparent' || color.includes('rgba(0, 0, 0, 0)');
        });

        // 当背景为透明或所有渐变色为透明时，container 也设为透明且移除阴影
        const isTransparentBg = resolvedBgColor === 'transparent' || isAllTransparent;
        const gradientContainerStyle = isTransparentBg
            ? {
                paddingTop: statusBarHeight,
                backgroundColor: 'transparent' as const,
                elevation: 0,
                shadowOpacity: 0,
            }
            : { paddingTop: statusBarHeight };

        return (
            <View style={[styles.container, gradientContainerStyle, style]}>
                <LinearGradient
                    colors={resolvedGradientColors}
                    style={[StyleSheet.absoluteFill, isAllTransparent && { opacity: 0 }]}
                    pointerEvents={isAllTransparent ? 'none' : 'auto'}
                    {...gradientProps}
                />
                <StatusBar backgroundColor="transparent" translucent barStyle={resolvedBarStyle} />
                {renderContentLayer()}
            </View>
        );
    }

    // ====== 普通背景模式 ======
    const navContainerStyle: StyleProp<ViewStyle> = [
        styles.container,
        {
            paddingTop: statusBarHeight,
            backgroundColor: resolvedBgColor,
        },
        style,
    ];

    return (
        <View style={navContainerStyle}>
            <StatusBar
                backgroundColor={resolvedBgColor}
                translucent
                barStyle={resolvedBarStyle}
            />
            {renderContentLayer()}
        </View>
    );
};

export default DbNavBar;
